import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';

/** Fallback E.164 if DOM source is empty or unparsable */
export const PHONE_E164 = '+18183009985';

/** Must match Google Ads “Displayed Phone Number to Replace” */
export const PHONE_DISPLAY = '(818) 300-9985';

/** Single global element id for Google website call / DNI */
export const GOOGLE_ADS_PHONE_SOURCE_ID = 'phone-number';

/**
 * Build tel: href from display text (Google may inject a forwarding number in any format).
 */
export const displayTextToTelHref = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `tel:+${digits}`;
  if (digits.length >= 8 && digits.length <= 15) return `tel:+${digits}`;
  return `tel:+${digits}`;
};

export const displayTextToE164Clipboard = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  return `+${digits}`;
};

const PhoneSourceContext = createContext(null);

/** Off-screen but still in layout flow for Google; avoid display:none */
const phoneSourceSpanStyle = {
  position: 'absolute',
  left: '-10000px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
};

/**
 * Mounts the single global #phone-number node and keeps tel/display state in sync
 * (MutationObserver, route changes, late Google tags).
 */
export function PhoneSourceProvider({ children }) {
  const location = useLocation();
  const phoneSourceRef = useRef(null);

  const [telHref, setTelHref] = useState(
    () => displayTextToTelHref(PHONE_DISPLAY) || `tel:${PHONE_E164}`
  );
  const [resolvedDisplay, setResolvedDisplay] = useState(PHONE_DISPLAY);

  const syncFromDom = useCallback(() => {
    const el =
      phoneSourceRef.current ?? document.getElementById(GOOGLE_ADS_PHONE_SOURCE_ID);
    const text = el?.textContent?.trim() ?? '';
    const nextDisplay = text || PHONE_DISPLAY;
    const href = displayTextToTelHref(nextDisplay) || `tel:${PHONE_E164}`;
    setResolvedDisplay(nextDisplay);
    setTelHref(href);
  }, []);

  // Initial paint + every client-side navigation (SPA): all Call Now buttons share this state
  useLayoutEffect(() => {
    syncFromDom();
  }, [syncFromDom, location.pathname]);

  useEffect(() => {
    const el =
      phoneSourceRef.current ?? document.getElementById(GOOGLE_ADS_PHONE_SOURCE_ID);
    if (!el) return undefined;

    const observer = new MutationObserver(() => {
      syncFromDom();
    });
    observer.observe(el, {
      characterData: true,
      childList: true,
      subtree: true
    });

    const onLoad = () => syncFromDom();
    window.addEventListener('load', onLoad);
    const t = window.setTimeout(syncFromDom, 2000);

    return () => {
      observer.disconnect();
      window.removeEventListener('load', onLoad);
      window.clearTimeout(t);
    };
  }, [syncFromDom]);

  const value = useMemo(
    () => ({
      telHref,
      resolvedDisplay,
      syncFromDom
    }),
    [telHref, resolvedDisplay, syncFromDom]
  );

  return (
    <PhoneSourceContext.Provider value={value}>
      <span
        id={GOOGLE_ADS_PHONE_SOURCE_ID}
        ref={phoneSourceRef}
        style={phoneSourceSpanStyle}
        aria-hidden
      >
        {PHONE_DISPLAY}
      </span>
      {children}
    </PhoneSourceContext.Provider>
  );
}

export function usePhoneSource() {
  const ctx = useContext(PhoneSourceContext);
  if (!ctx) {
    throw new Error('usePhoneSource must be used within PhoneSourceProvider');
  }
  return ctx;
}
